import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setMessageAction } from '@/redux/slicers/api-message.slice';
import { setLoadingAction } from '@/redux/slicers/loading.slice';
import { tick } from '@/utils/common';
import { API } from '@/utils/axios';
import { Messages } from '@/constants/message';
import { AxiosRequestConfig, ResponseType } from 'axios';

export function useApi(basePath: string) {
  const axios = API;

  return useMemo(() => {
    return {
      get: (path: string, query: Record<string, any> = {}, responseType?: ResponseType): any => {
        const url = path.startsWith('http') ? path : `${axios.defaults.baseURL}${basePath}${path}`;
        return axios.get(url, { params: query, responseType });
      },
      post: (
        path: string,
        body: Record<string, any> | FormData = {},
        config?: AxiosRequestConfig,
      ): any => {
        const url = path.startsWith('http') ? path : `${axios.defaults.baseURL}${basePath}${path}`;
        return axios.post(url, body, config);
      },
      put: (path: string, body: Record<string, any> | FormData = {}): any => {
        const url = path.startsWith('http') ? path : `${axios.defaults.baseURL}${basePath}${path}`;
        return axios.put(url, body);
      },
      patch: (path: string, body: Record<string, any> | FormData = {}): any => {
        const url = path.startsWith('http') ? path : `${axios.defaults.baseURL}${basePath}${path}`;
        return axios.patch(url, body);
      },
      del: (path: string, query: Record<string, any> = {}, data: Record<string, any> = {}): any => {
        const url = path.startsWith('http') ? path : `${axios.defaults.baseURL}${basePath}${path}`;
        return axios.delete(url, { params: query, data: data });
      },
    };
  }, [basePath, axios]);
}

interface CallerOptions {
  messageKey?: string;
  loadingKey?: string;
  successMessage?: string;
  errorMessage?: string;
  delay?: number;
}

type Caller = <T = any>(
  func: () => Promise<T | null | undefined>,
  options?: CallerOptions,
) => Promise<T | null | undefined>;

export function useCaller() {
  const dispatch = useDispatch();

  const setMessage = useCallback(
    (key: string, info?: MessageOptions) => {
      dispatch(setMessageAction([key, info]));
    },
    [dispatch],
  );

  const setLoading = useCallback(
    (key: string, el?: boolean) => {
      dispatch(setLoadingAction([key, el]));
    },
    [dispatch],
  );

  const caller = useCallback<Caller>(
    async (
      func,
      {
        successMessage = '',
        errorMessage,
        messageKey = 'global',
        loadingKey = 'global',
        delay,
      } = {},
    ) => {
      messageKey && setMessage(messageKey);
      try {
        setLoading(loadingKey, true);

        const [res]: any = await Promise.all([func(), delay != null ? tick(delay) : undefined]);

        // Backend response format:
        // res.data = { content, succeeded, messages, totalElements, ... }
        const responseData = res?.data;

        // Debug logging

        // Check for error conditions
        const isError =
          res === undefined ||
          responseData === undefined ||
          responseData.succeeded === false ||
          res.exception === 'Error' ||
          res.status >= 400 ||
          res.statusCode >= 400;

        if (isError) {
          if (messageKey) {
            // Get error messages from response.messages array or use default
            const errorMessages =
              responseData?.messages && Array.isArray(responseData.messages)
                ? responseData.messages
                : errorMessage
                ? [errorMessage]
                : [Messages.API_ERROR];

            setMessage(messageKey, {
              type: 'error',
              messages: errorMessages,
            });
          }
          setTimeout(() => setLoading(loadingKey, false), 0);
          return responseData ?? null;
        }

        // Show success message if provided
        if (successMessage !== '') {
          if (messageKey) {
            // Check if there are error messages in response
            const hasErrorMessages =
              responseData?.messages &&
              Array.isArray(responseData.messages) &&
              responseData.messages.length > 0;

            if (hasErrorMessages) {
              setMessage(messageKey, {
                type: 'error',
                messages: responseData.messages,
              });
            } else {
              setMessage(messageKey, {
                type: 'success',
                messages: [successMessage],
              });
            }
          }
        }
        setTimeout(() => setLoading(loadingKey, false), 0);

        return responseData;
      } catch (error: any) {
        if (messageKey) {
          const errors = error.response?.data?.message || error.response?.data?.error;
          setMessage(messageKey, {
            type: 'error',
            messages: errorMessage ? [errorMessage] : errors ? [errors] : [Messages.API_ERROR],
          });
        }
        setTimeout(() => setLoading(loadingKey, false), 0);
        return error.response?.data ?? null;
      }
    },
    [setLoading, setMessage],
  );
  return caller;
}
